import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import Card from './Card';
import Button from './Button';
import Loading from './Loading';
import EmptyState from './EmptyState';
import Alert from './Alert';
import CreateRuleModal from './CreateRuleModal';
import EditRuleModal from './EditRuleModal';
import DeleteRuleDialog from './DeleteRuleDialog';
import ContributorFeedbackModal from './ContributorFeedbackModal';
import AddIcon from '@mui/icons-material/Add';
import PublicIcon from '@mui/icons-material/Public';
import CategoryIcon from '@mui/icons-material/Category';
import RuleIcon from '@mui/icons-material/Rule';
import LabelIcon from '@mui/icons-material/Label';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';
import api from '../services/api';

const PAGE_TYPE_FILTERS = [
  { value: null, label: 'All' },
  { value: 'detail', label: 'Detail' },
  { value: 'search', label: 'Search' },
];

const PAGE_TYPE_COLORS = {
  detail: { bg: 'rgba(99, 102, 241, 0.15)', color: '#818cf8', border: 'rgba(99, 102, 241, 0.3)' },
  search: { bg: 'rgba(245, 158, 11, 0.15)', color: '#fbbf24', border: 'rgba(245, 158, 11, 0.3)' },
};

function PageTypeBadge({ pageType }) {
  const style = PAGE_TYPE_COLORS[pageType] || PAGE_TYPE_COLORS.detail;
  return (
    <Chip
      label={pageType}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: '0.65rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        background: style.bg,
        color: style.color,
        border: `1px solid ${style.border}`,
        height: 22,
      }}
    />
  );
}

function RetailerBadge({ retailer }) {
  if (!retailer) return null;
  return (
    <Chip
      label={retailer}
      size="small"
      sx={{
        fontWeight: 600,
        fontSize: '0.65rem',
        textTransform: 'none',
        letterSpacing: '0.3px',
        background: 'rgba(20, 184, 166, 0.15)',
        color: '#2dd4bf',
        border: '1px solid rgba(20, 184, 166, 0.3)',
        height: 22,
      }}
    />
  );
}

const CONTRIBUTOR_LABELS = {
  primary: { label: 'Primary', color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)' },
  grouped: { label: 'Grouped', color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.12)' },
  inherited: { label: 'Inherited', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.12)' },
  manual: { label: 'Manual', color: '#a855f7', bg: 'rgba(168, 85, 247, 0.12)' },
};


function RuleCard({ rule, contributors = [], onEdit, onDelete, onContributorClick }) {
  const hasContributors = contributors.length > 0;
  const isServiceCreated = rule.created_by === 'rule_synthesis_service';
  const uniqueReviewers = [...new Map(contributors.map((c) => [c.reviewed_by, c])).values()];

  return (
    <Box
      sx={{
        p: 2,
        background: '#0f172a',
        borderLeft: '3px solid #3b82f6',
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.2s ease',
        '&:hover': {
          background: '#1e293b',
          border: '1px solid rgba(59, 130, 246, 0.3)',
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, flex: 1 }}>
          <RuleIcon sx={{ color: '#3b82f6', mt: 0.5 }} fontSize="small" />
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <PageTypeBadge pageType={rule.page_type} />
              <RetailerBadge retailer={rule.retailer} />
            </Box>
            <Typography 
              variant="body1" 
              sx={{ color: '#cbd5e1', lineHeight: 1.6, mb: 1 }}
            >
              {rule.rule_text}
            </Typography>
            {hasContributors ? (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.75 }}>
                  Contributors
                  {rule.synthesis_action || (isServiceCreated && contributors[0]?.synthesis_action)
                    ? ` · ${(contributors[0]?.synthesis_action || 'NEW').toUpperCase()}`
                    : ''}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {uniqueReviewers.map((c) => {
                    const style = CONTRIBUTOR_LABELS[c.contribution_type] || CONTRIBUTOR_LABELS.manual;
                    return (
                      <Chip
                        key={c.reviewed_by}
                        icon={<PersonIcon sx={{ color: `${style.color} !important`, fontSize: '0.875rem' }} />}
                        label={c.reviewed_by}
                        size="small"
                        clickable
                        onClick={() => onContributorClick(rule, c.reviewed_by)}
                        sx={{
                          height: 22,
                          fontSize: '0.72rem',
                          fontWeight: 600,
                          color: style.color,
                          background: style.bg,
                          border: `1px solid ${style.color}33`,
                          '&:hover': {
                            background: `${style.color}22`,
                          },
                        }}
                      />
                    );
                  })}
                </Box>
                {isServiceCreated && (
                  <Typography variant="caption" sx={{ color: '#475569', fontSize: '0.68rem', display: 'block', mt: 0.75 }}>
                    Generated by rule synthesis service
                  </Typography>
                )}
              </Box>
            ) : rule.created_by ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                <PersonIcon sx={{ color: '#64748b', fontSize: '0.875rem' }} />
                <Typography 
                  variant="caption" 
                  sx={{ color: '#64748b', fontSize: '0.75rem' }}
                >
                  Created by {rule.created_by}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }}>
          <Tooltip title="Edit rule">
            <IconButton 
              size="small" 
              sx={{ 
                color: '#64748b',
                '&:hover': { color: '#3b82f6' }
              }}
              onClick={() => onEdit(rule)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete rule">
            <IconButton 
              size="small" 
              sx={{ 
                color: '#64748b',
                '&:hover': { color: '#ef4444' }
              }}
              onClick={() => onDelete(rule)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}

function RulesTab() {
  const [globalRules, setGlobalRules] = useState([]);
  const [attributeRules, setAttributeRules] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createRuleModalOpen, setCreateRuleModalOpen] = useState(false);
  const [editRuleModalOpen, setEditRuleModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [pageTypeFilter, setPageTypeFilter] = useState(null);
  const [retailerFilter, setRetailerFilter] = useState('');
  const [availableRetailers, setAvailableRetailers] = useState([]);
  const [contributorsByRuleId, setContributorsByRuleId] = useState({});
  const [contributorModalOpen, setContributorModalOpen] = useState(false);
  const [contributorModalData, setContributorModalData] = useState({
    reviewedBy: null,
    entries: [],
    loading: false,
    error: null,
  });

  useEffect(() => {
    api.getAnomalyFilters().then((data) => {
      setAvailableRetailers(data.retailers || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadRules(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTypeFilter, retailerFilter]);

  const fetchContributorsForRules = async (rules) => {
    if (!rules.length) {
      setContributorsByRuleId({});
      return;
    }
    const entries = await Promise.all(
      rules.map(async (rule) => {
        try {
          const data = await api.getRuleContributors(rule.id);
          return [rule.id, data.contributors || []];
        } catch {
          return [rule.id, []];
        }
      }),
    );
    setContributorsByRuleId(Object.fromEntries(entries));
  };

  const loadRules = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    
    try {
      const retailer = retailerFilter || null;
      const [global, attributes] = await Promise.all([
        api.getGlobalRules(pageTypeFilter, retailer),
        api.getAllAttributeRules(pageTypeFilter, retailer),
      ]);
      
      setGlobalRules(global);
      setAttributeRules(attributes);

      const allRules = [
        ...global,
        ...Object.values(attributes).flat(),
      ];
      await fetchContributorsForRules(allRules);
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const handleRuleCreated = () => {
    setCreateRuleModalOpen(false);
    loadRules(true);
  };

  const handleEditClick = (rule) => {
    setSelectedRule(rule);
    setEditRuleModalOpen(true);
  };

  const handleDeleteClick = (rule) => {
    setSelectedRule(rule);
    setDeleteDialogOpen(true);
  };

  const handleRuleUpdated = () => {
    setEditRuleModalOpen(false);
    setSelectedRule(null);
    loadRules(true);
  };

  const handleRuleDeleted = () => {
    setDeleteDialogOpen(false);
    setSelectedRule(null);
    loadRules(true);
  };

  const handleContributorClick = async (rule, reviewedBy) => {
    setContributorModalOpen(true);
    setContributorModalData({ reviewedBy, entries: [], loading: true, error: null });
    try {
      const data = await api.getContributorFeedback(rule.id, reviewedBy);
      setContributorModalData({ reviewedBy, entries: data.entries || [], loading: false, error: null });
    } catch (err) {
      setContributorModalData({ reviewedBy, entries: [], loading: false, error: err.message });
    }
  };

  const handleContributorModalClose = () => {
    setContributorModalOpen(false);
  };

  if (loading) {
    return <Loading message="Loading rules..." />;
  }

  if (error) {
    return <Alert variant="error">Error loading rules: {error}</Alert>;
  }

  const hasRules = globalRules.length > 0 || Object.keys(attributeRules).length > 0;

  const totalRulesCount = globalRules.length + Object.values(attributeRules).flat().length;

  return (
    <>
      <Box className="space-y-6">
        {/* Header Section */}
        <Box className="flex items-center justify-between">
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#e2e8f0', mb: 1 }}>
              Active Rules
            </Typography>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              {totalRulesCount === 0
                ? 'No rules created yet'
                : `${totalRulesCount} active ${totalRulesCount === 1 ? 'rule' : 'rules'}${pageTypeFilter ? ` · ${pageTypeFilter}` : ''}${retailerFilter ? ` · ${retailerFilter}` : ''}`
              }
            </Typography>
          </Box>
          <Button 
            onClick={() => setCreateRuleModalOpen(true)}
            startIcon={<AddIcon />}
            size="large"
          >
            Create Rule
          </Button>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={pageTypeFilter}
            exclusive
            onChange={(_, value) => {
              if (value !== undefined) setPageTypeFilter(value);
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                color: '#94a3b8',
                borderColor: 'rgba(255, 255, 255, 0.15)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                px: 2,
                '&.Mui-selected': {
                  color: '#60a5fa',
                  background: 'rgba(59, 130, 246, 0.15)',
                  borderColor: 'rgba(59, 130, 246, 0.4)',
                  '&:hover': {
                    background: 'rgba(59, 130, 246, 0.25)',
                  },
                },
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.05)',
                },
              },
            }}
          >
            {PAGE_TYPE_FILTERS.map((f) => (
              <ToggleButton key={f.value ?? 'all'} value={f.value}>
                {f.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {availableRetailers.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel
                sx={{
                  color: '#94a3b8',
                  '&.Mui-focused': { color: '#60a5fa' },
                }}
              >
                Retailer
              </InputLabel>
              <Select
                value={retailerFilter}
                label="Retailer"
                onChange={(e) => setRetailerFilter(e.target.value)}
                sx={{
                  color: '#e2e8f0',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.15)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(59, 130, 246, 0.6)',
                  },
                  '& .MuiSvgIcon-root': { color: '#94a3b8' },
                  background: 'rgba(255, 255, 255, 0.04)',
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      background: '#1e293b',
                      border: '1px solid rgba(255,255,255,0.1)',
                      '& .MuiMenuItem-root': {
                        color: '#cbd5e1',
                        fontSize: '0.85rem',
                        '&:hover': { background: 'rgba(59,130,246,0.1)' },
                        '&.Mui-selected': { background: 'rgba(59,130,246,0.2)', color: '#60a5fa' },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">All Retailers</MenuItem>
                {availableRetailers.map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {!hasRules ? (
          <Card>
            <EmptyState
              message={pageTypeFilter || retailerFilter ? `No rules match current filters` : 'No rules created yet'}
              description="Create your first rule to start validating anomalies and improving accuracy"
            />
          </Card>
        ) : (
          <Grid container spacing={3}>
            {/* Global Rules Section */}
            {globalRules.length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <Box className="mb-4">
                    <Box className="flex items-center gap-2 mb-2">
                      <PublicIcon sx={{ color: '#3b82f6' }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#e2e8f0' }}>
                        Global Rules
                      </Typography>
                      <Chip 
                        label={globalRules.length} 
                        size="small" 
                        sx={{ 
                          fontWeight: 600,
                          background: 'rgba(59, 130, 246, 0.2)',
                          color: '#60a5fa',
                          border: '1px solid rgba(59, 130, 246, 0.3)'
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Rules that apply across all attributes
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
                  
                  <Box className="space-y-3">
                    {globalRules.map((rule) => (
                      <RuleCard
                        key={rule.id}
                        rule={rule}
                        contributors={contributorsByRuleId[rule.id] || []}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick}
                        onContributorClick={handleContributorClick}
                      />
                    ))}
                  </Box>
                </Card>
              </Grid>
            )}

            {/* Attribute-Specific Rules Section */}
            {Object.keys(attributeRules).length > 0 && (
              <Grid item xs={12}>
                <Card>
                  <Box className="mb-4">
                    <Box className="flex items-center gap-2 mb-2">
                      <CategoryIcon sx={{ color: '#3b82f6' }} />
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#e2e8f0' }}>
                        Attribute-Specific Rules
                      </Typography>
                      <Chip 
                        label={Object.keys(attributeRules).length} 
                        size="small" 
                        sx={{ 
                          fontWeight: 600,
                          background: 'rgba(59, 130, 246, 0.2)',
                          color: '#60a5fa',
                          border: '1px solid rgba(59, 130, 246, 0.3)'
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      Rules that apply to specific product attributes
                    </Typography>
                  </Box>
                  
                  <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 2 }} />
                  
                  <Box className="space-y-6">
                    {Object.entries(attributeRules).map(([attribute, rules]) => (
                      <Box key={attribute}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, pb: 1.5, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <LabelIcon sx={{ color: '#3b82f6', fontSize: '1.125rem' }} />
                          <Typography 
                            variant="subtitle1" 
                            sx={{ 
                              fontWeight: 700,
                              color: '#60a5fa',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              fontSize: '0.875rem'
                            }}
                          >
                            {attribute}
                          </Typography>
                          <Chip 
                            label={`${rules.length} ${rules.length === 1 ? 'rule' : 'rules'}`}
                            size="small"
                            sx={{ 
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              background: 'rgba(59, 130, 246, 0.15)',
                              color: '#60a5fa',
                              border: '1px solid rgba(59, 130, 246, 0.3)'
                            }}
                          />
                        </Box>
                        
                        <Box className="space-y-3 ml-6">
                          {rules.map((rule) => (
                            <RuleCard
                              key={rule.id}
                              rule={rule}
                              contributors={contributorsByRuleId[rule.id] || []}
                              onEdit={handleEditClick}
                              onDelete={handleDeleteClick}
                              onContributorClick={handleContributorClick}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      {createRuleModalOpen && (
        <CreateRuleModal
          isOpen={createRuleModalOpen}
          onClose={() => setCreateRuleModalOpen(false)}
          onRuleCreated={handleRuleCreated}
        />
      )}

      {editRuleModalOpen && (
        <EditRuleModal
          isOpen={editRuleModalOpen}
          onClose={() => {
            setEditRuleModalOpen(false);
            setSelectedRule(null);
          }}
          onRuleUpdated={handleRuleUpdated}
          rule={selectedRule}
        />
      )}

      {deleteDialogOpen && (
        <DeleteRuleDialog
          isOpen={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setSelectedRule(null);
          }}
          onRuleDeleted={handleRuleDeleted}
          rule={selectedRule}
        />
      )}

      {contributorModalOpen && (
        <ContributorFeedbackModal
          isOpen={contributorModalOpen}
          onClose={handleContributorModalClose}
          reviewedBy={contributorModalData.reviewedBy}
          entries={contributorModalData.entries}
          loading={contributorModalData.loading}
          error={contributorModalData.error}
        />
      )}
    </>
  );
}

export default RulesTab;
